import {
  Loadable,
  Model,
  ModelAttributes,
  ModelDescription,
  model,
  modelConfig,
  reference,
} from "@mixitone/oom";
import ClubPlayer from "./ClubPlayer";
import { setAccountId } from "./concerns/SetAccountId";
import Night from "./Night";

const config = modelConfig(
  {
    account_id: {
      type: "string",
    },
    active: {
      type: "boolean",
    },
    club_player_id: {
      type: "string",
    },
    created_at: {
      type: "datetime",
    },
    index: {
      type: "number",
    },
    is_deleted: {
      type: "boolean",
    },
    joined_game_set_index: {
      type: "number",
    },
    name: {
      type: "string",
    },
    night_id: {
      type: "string",
    },
    paid: {
      type: "boolean",
    },
    partner_id: {
      type: "string",
    },
    retired_game_set_index: {
      type: "number",
    },
    updated_at: {
      type: "datetime",
    },
  },
  {},
  {
    night: reference<any>(() => Night as any, "night_id"),
    clubPlayer: reference<any>(() => ClubPlayer as any, "club_player_id"),
  },
);

interface Player extends ModelAttributes<typeof config> {
  night: Loadable<Night>;
  get clubPlayer(): Loadable<ClubPlayer>;
  set clubPlayer(value: ClubPlayer);
}

@setAccountId
@model("players", config)
class Player extends Model<typeof config> {
  static readonly modelDescription: ModelDescription = {
    description:
      "A player is a join record between club players and nights indiciating a club player is participating in a night (session).",
    availableFields: Object.keys(config.fields),
    relationships: {
      night: "nights where id equals this night_id",
      clubPlayer:
        "club_players where id equals this club_player_id. This is the club player that the player is associated with.",
      games:
        "games where player_id equals this id. These are the games that the player has played in the night.",
    },
  };

  get group() {
    if (!this.clubPlayer.loaded) {
      console.trace("Club player not loaded");
      ClubPlayer.query()
        .find(this.club_player_id!)
        .then((cp) => {
          console.log("the club player is", cp.name);
        });

      throw new Error("Club player not loaded");
    }
    return this.clubPlayer.group;
  }

  get timesPlayed() {
    return this.night.game_sets.filter((gs) => gs.started && gs.hasPlayer(this.id!)).length;
  }

  get timesOut() {
    return this.night.game_sets.filter((gs) => gs.started).length - this.timesPlayed;
  }

  get gameSets() {
    return this.night.game_sets.filter((gs) => gs.hasPlayer(this.id!));
  }

  get games() {
    return this.gameSets.flatMap((gs) => gs.games).filter((g) => g.hasPlayer(this.id!));
  }

  get name() {
    if (!this.clubPlayer.loaded) throw new Error("Club player not loaded");
    return this.clubPlayer.name;
  }

  set name(value: string | undefined) {
    if (!this.clubPlayer.loaded) throw new Error("Club player not loaded");
    this.clubPlayer.name = value;
    this.writeAttribute("name", value);
  }

  get isPaired() {
    return !!this.partner_id;
  }
}

export type PlayerPair = [Player, Player];
export type PlayerSingle = [Player];
export type PlayerContainer = PlayerPair | PlayerSingle;
export type PlayerPairs = PlayerContainer[];
export function isPlayerPair(players: PlayerContainer | Player): players is PlayerPair {
  return Array.isArray(players) && Player.is(players[0]) && players.length === 2;
}
export function isPlayerContainer(players: any): players is PlayerContainer {
  return isPlayerPair(players) || (Array.isArray(players) && Player.is(players[0]));
}

export default Player;
